import * as React from "react";
import { useMsal } from "@azure/msal-react";
import { AdContext } from "../context/AdContext";
import styles from "../styles/layout.module.css";

const COUNTDOWN = 5;

interface Props {
  onToggleSidebar: () => void;
  sidebarCollapsed: boolean;
}

const Header = ({ onToggleSidebar, sidebarCollapsed }: Props) => {
  const { email } = React.useContext(AdContext);
  const { instance } = useMsal();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [showLogoutModal, setShowLogoutModal] = React.useState(false);
  const [countdown, setCountdown] = React.useState(COUNTDOWN);
  const menuRef = React.useRef<HTMLDivElement>(null);

  const initials = email
    ? email.split("@")[0].split(".").map((s) => s[0]).join("").slice(0, 2)
    : "U";

  // Countdown tick — fires only while modal is open
  React.useEffect(() => {
    if (!showLogoutModal) return;
    if (countdown <= 0) {
      instance.clearCache().finally(() => {
        window.location.href = "/login";
      });
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [showLogoutModal, countdown, instance]);

  const openLogoutModal = () => {
    setMenuOpen(false);
    setCountdown(COUNTDOWN);
    setShowLogoutModal(true);
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
    setCountdown(COUNTDOWN);
  };

  const confirmLogout = () => {
    setCountdown(0);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  return (
    <>
      <header className={styles.appHeader}>
        <div className={`${styles.headerLogo}${sidebarCollapsed ? ` ${styles.sidebarCollapsed}` : ""}`}>
          <span className={styles.headerLogoText}>
            {sidebarCollapsed ? "QT" : "Qtrust"}
          </span>
          <button
            className={styles.headerHamburger}
            onClick={onToggleSidebar}
            aria-label="Toggle sidebar"
            aria-expanded={!sidebarCollapsed}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <rect y="3" width="20" height="2" rx="1" fill="currentColor" />
              <rect y="9" width="20" height="2" rx="1" fill="currentColor" />
              <rect y="15" width="20" height="2" rx="1" fill="currentColor" />
            </svg>
          </button>
        </div>

        <div className={styles.headerSpacer} />

        <div className={styles.headerUser} ref={menuRef} style={{ position: "relative" }}>
          {email && <span className={styles.headerUserEmail}>{email}</span>}
          <div
            className={styles.headerAvatar}
            aria-label={`User: ${email}`}
            onClick={() => setMenuOpen((o) => !o)}
            style={{ cursor: "pointer" }}
            title="Account options"
          >
            {initials}
          </div>

          {menuOpen && (
            <div className={styles.userMenu}>
              <div className={styles.userMenuEmail}>{email}</div>
              <div className={styles.userMenuDivider} />
              <button className={styles.userMenuLogout} onClick={openLogoutModal}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                Sign out
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Logout confirmation overlay */}
      {showLogoutModal && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 1000,
          background: "rgba(0,0,0,0.55)", backdropFilter: "blur(3px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: "'Inter', sans-serif",
        }}>
          <div style={{
            background: "#fff", borderRadius: 16, padding: "40px 36px",
            maxWidth: 380, width: "100%", textAlign: "center",
            boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
            display: "flex", flexDirection: "column", alignItems: "center", gap: 20,
          }}>
            {/* Icon */}
            <div style={{
              width: 56, height: 56, borderRadius: "50%",
              background: "rgba(239,68,68,0.1)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </div>

            {/* Text */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>
                Sign out?
              </div>
              <div style={{ fontSize: 13, color: "#5A6578", lineHeight: 1.6 }}>
                You'll be signed out in{" "}
                <strong style={{ color: "#EF4444" }}>{countdown}</strong>{" "}
                second{countdown !== 1 ? "s" : ""}. Cancel to stay signed in.
              </div>
            </div>

            {/* Progress bar */}
            <div style={{ width: "100%", height: 3, background: "#F3F4F6", borderRadius: 2, overflow: "hidden" }}>
              <div style={{
                height: "100%", background: "#EF4444", borderRadius: 2,
                width: `${(countdown / COUNTDOWN) * 100}%`,
                transition: "width 1s linear",
              }} />
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: 10, width: "100%" }}>
              <button
                onClick={cancelLogout}
                style={{
                  flex: 1, padding: "10px 0", borderRadius: 8, border: "1px solid #DDE1E8",
                  background: "#fff", color: "#111827", fontSize: 14, fontWeight: 600, cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                style={{
                  flex: 1, padding: "10px 0", borderRadius: 8, border: "none",
                  background: "#EF4444", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer",
                }}
              >
                Sign out now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
